import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";

import { CurrencyFormatter } from "../../helpers/currencyFormatter";
import { VariantSelector } from "./VariantSelector";
import ProductImageGallery from "./ProductImage";
import { showToast } from "../../toast/toastManager";
import { fetchCarrito, addItemToCarrito, removeItemFromCarrito, updateItemQuantity } from "../../hooks/use-FetchCarrito";
import { fetchSession } from "../../hooks/use-FetchSession";


import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function ProductModal2({ show, onHide, product, currency }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // ==============================
  // Redux state
  // ==============================
  const { session } = useSelector((state) => state.session);
  const { carrito } = useSelector((state) => state.carrito);
  const { articleDetail } = useSelector((state) => state.articleDetail);

  
  const carritoRef = carrito?.carritoRef ?? session?.carritoRef;

  // ==============================
  // Local state
  // ==============================
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [productStock, setProductStock] = useState(0);
  const [quantityCount, setQuantityCount] = useState(1);
  const [selectedVariantImage, setSelectedVariantImage] = useState([]);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

      
  // ==============================
  // Effects
  // ==============================
  useEffect(() => {
    if (show && !session?.sessionId) {
      dispatch(fetchSession());
    }
  }, [dispatch, session?.sessionId, show]);

  useEffect(() => {
    if (show && carrito == null && session?.sessionId) {
      dispatch(fetchCarrito(session.sessionId, session?.usuarioRef, session?.carritoRef));
    }
  }, [carrito, dispatch, session?.carritoRef, session?.sessionId, session?.usuarioRef, show]);
  // useEffect(() => {
  //     dispatch(fetchSession())
    
  //   if (articleDetail?.variants?.length > 0) {
  //     const variant = articleDetail.variants[0];
  //     setSelectedVariant(variant);
  //     setProductStock(variant.stock);
  //     setSelectedVariantImage(variant.images);
  //     setQuantityCount(1);
  //   }
  // }, [articleDetail]);

  // ==============================
  // Handlers
  // ==============================
  const handleAddToCart = async () => {
    if (!selectedVariant || productStock <= 0) return;

    const codigoInterno = selectedVariant.code ?? selectedVariant.codigoInterno ?? articleDetail?.code;
    let activeSession = session;
    let activeCarritoRef = carritoRef;

    if (!activeSession?.sessionId) {
      const sessionResponse = await dispatch(fetchSession());
      activeSession = sessionResponse?.data;
    }

    if (!activeCarritoRef && activeSession?.sessionId) {
      const carritoResponse = await dispatch(
        fetchCarrito(activeSession.sessionId, activeSession?.usuarioRef, activeSession?.carritoRef)
      );
      activeCarritoRef = carritoResponse?.data?.carrito?.carritoRef;
    }

    if (!activeCarritoRef) {
      showToast("No se pudo identificar el carrito", "error", "top-center");
      return;
    }

    if (!codigoInterno) {
      showToast("No se pudo identificar el producto", "error", "top-center");
      return;
    }

    if (quantityCount > productStock) {
      showToast(
        `Cantidad excede el stock disponible (${productStock})`,
        "warn",
        "top-center"
      );
      return;
    }

    try {
      await dispatch(
        addItemToCarrito(
          activeCarritoRef,
          codigoInterno,
          quantityCount
        )
      );

      showToast("Producto agregado al carrito", "success", "top-center");
    } catch {
      showToast("Error al agregar al carrito", "error", "top-center");
    }
  };

  const handleBuyNow = async () => {
    const codigoInterno = selectedVariant?.code ?? selectedVariant?.codigoInterno ?? articleDetail?.code;

    if (!selectedVariant || productStock <= 0 || !codigoInterno) return;

    let activeSession = session;
    let activeCarritoRef = carritoRef;

    if (!activeSession?.sessionId) {
      const sessionResponse = await dispatch(fetchSession());
      activeSession = sessionResponse?.data;
    }

    if (!activeCarritoRef && activeSession?.sessionId) {
      const carritoResponse = await dispatch(
        fetchCarrito(activeSession.sessionId, activeSession?.usuarioRef, activeSession?.carritoRef)
      );
      activeCarritoRef = carritoResponse?.data?.carrito?.carritoRef;
    }

    if (!activeCarritoRef) return;

    await dispatch(
      addItemToCarrito(
        activeCarritoRef,
        codigoInterno,
        quantityCount
      )
    );

    navigate("/cart");
  };

  const onCloseModal = () => {
    setThumbsSwiper(null);
    onHide();
  };

  const canAddToCart = productStock > 0 && quantityCount <= productStock;

  // ==============================
  // Render
  // ==============================
  return (
    <Modal
      show={show}
      onHide={onCloseModal}
      className="product-quickview-modal-wrapper"
      centered
    >
      <Modal.Header closeButton />

      <Modal.Body>
        <div className="modal-body py-0">
          <div className="row">
            {/* IMÁGENES */}
            <div className="co-lg-5 col-md-6 col-sm-6 col-xs-12">
              <div className="product-large-image-wrapper px-4">
                <ProductImageGallery images={selectedVariantImage} />
              </div>
            </div>

            {/* DETALLE */}
            <div className="co-lg-7 col-md-6 col-sm-6 col-xs-12">
              <div className="product-details-content quickview-content">
                <Link to={`/producto/${articleDetail?.sku}`}>
                  <h2>{articleDetail?.name}</h2>
                </Link>

                <ProductModalPriceRows
                  product={articleDetail}
                  fallbackProduct={product}
                  selectedVariant={selectedVariant}
                  i18n={i18n}
                  currency={currency}
                />

                {/* VARIANTES */}
                <VariantSelector
                  articleDetail={articleDetail}
                  setSelectedVariant={setSelectedVariant}
                  setProductStock={setProductStock}
                  setQuantityCount={setQuantityCount}
                  setSelectedVariantImage={setSelectedVariantImage}
                />

                <small>
                  <b>SKU:</b> {articleDetail?.sku}
                </small>

                {/* CANTIDAD */}
                <div className="pro-details-quality tc-modal-quantity mt-3">
                  {productStock > 0 ? (
                    <div className="tc-modal-quantity-control">
                      <span className="tc-modal-quantity-label">Cantidad:</span>
                      <div className="cart-plus-minus">
                      <button
                        className="dec qtybutton"
                        disabled={quantityCount <= 1}
                        onClick={() =>
                          setQuantityCount((q) => Math.max(1, q - 1))
                        }
                      >
                        -
                      </button>

                      <input
                        className="cart-plus-minus-box text-center"
                        type="number"
                        value={quantityCount}
                        readOnly
                      />

                      <button
                        className="inc qtybutton"
                        onClick={() => {
                          if (quantityCount >= productStock) {
                            showToast(
                              `Stock máximo disponible: ${productStock}`,
                              "warn",
                              "top-center"
                            );
                            return;
                          }
                          setQuantityCount((q) => q + 1);
                        }}
                      >
                        +
                      </button>
                      </div>
                    </div>
                  ) : (
                    <span className="fs-4 fw-bold border p-2">
                      Sin existencias
                    </span>
                  )}
                </div>

                {/* BOTONES */}
                <div className="pro-details-cart mt-2">
                  <div className="d-flex gap-4">
                    <button
                      className="px-4 py-2 tc-modal-add-cart-btn"
                      onClick={handleAddToCart}
                      disabled={!canAddToCart}
                    >
                      <i className="fa fa-shopping-cart" aria-hidden="true" />
                      Añadir al carrito
                    </button>

                    {/* OPCIONAL */}
                    {/* 
                    <button
                      className="px-4 py-3 button-active-hs btn-black"
                      onClick={handleBuyNow}
                      disabled={!canAddToCart}
                    >
                      Comprar ahora
                    </button> 
                    */}
                  </div>
                </div>

                {/* DESCRIPCIÓN */}
                <div className="pro-details-list mt-3">
                  <details>
                    <summary>Descripción</summary>
                    <p className="text-break">
                      {cleanProductDescription(articleDetail?.shortDescription)}
                    </p>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

ProductModal2.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  product: PropTypes.object,
  currency: PropTypes.object,
};

export default ProductModal2;

const ProductModalPriceRows = ({ product, fallbackProduct, selectedVariant, i18n, currency }) => {
  const rate = currency?.currencyRate ?? 1;
  const cash = +((product?.price || fallbackProduct?.price || selectedVariant?.price || 0) * rate).toFixed(2);
  const debit = +((product?.price1 || fallbackProduct?.price1 || selectedVariant?.price1 || 0) * rate).toFixed(2);
  const credit = +((product?.price2 || fallbackProduct?.price2 || selectedVariant?.price2 || 0) * rate).toFixed(2);

  return (
    <div className="product-details-price tc-product-prices tc-product-prices--modal">
      {cash > 0 && (
        <span className="tc-product-price-row tc-product-price-row--cash">
          <span className="tc-product-price-label">Beneficio en Efectivo</span>
          <span className="tc-product-price-amount">{CurrencyFormatter(cash, i18n, currency)}</span>
        </span>
      )}
      {debit > 0 && (
        <span className="tc-product-price-row">
          <span className="tc-product-price-label">Tarjeta Débito</span>
          <span className="tc-product-price-amount">{CurrencyFormatter(debit, i18n, currency)}</span>
        </span>
      )}
      {credit > 0 && (
        <span className="tc-product-price-row">
          <span className="tc-product-price-label">Tarjeta Crédito</span>
          <span className="tc-product-price-amount">{CurrencyFormatter(credit, i18n, currency)}</span>
        </span>
      )}
    </div>
  );
};

const cleanProductDescription = (description = "") =>
  description
    .replace(
      /\s*(?:Oferta efectivo|Beneficio en Efectivo|Tarjeta contado|Tarjeta Debito|Tarjeta Débito|Tarjeta Credito|Tarjeta Crédito|Tarjeta cuotas)\s+Q[\d,.]+\.?/gi,
      ""
    )
    .replace(/\s{2,}/g, " ")
    .trim();
